import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, allComics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [filtered, setFiltered] = useState<Comic[]>([]);
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 12;

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    let result = slug === 'all' ? [...allComics] : allComics.filter((c) => c.category === slug);

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'views':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'latest':
      default:
        result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
    }

    setFiltered(result);
    setCurrentPage(1);
  }, [slug, sortBy]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginatedComics = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: currentCategory.name === '全部' ? '分类浏览' : currentCategory.name },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `韩国漫画网 - ${currentCategory.name}漫画`,
            description: `浏览${currentCategory.name}类型的韩国漫画，免费在线观看海量${currentCategory.name}韩漫资源`,
            url: `https://www.koreanmanhwa.cn/category/${slug}`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.koreanmanhwa.cn/' },
                { '@type': 'ListItem', position: 2, name: currentCategory.name + '漫画' },
              ],
            },
          }),
        }}
      />

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb items={breadcrumbItems} />

        <div className="mt-6">
          <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
            {currentCategory.name === '全部' ? '漫画大全' : currentCategory.name + '漫画'}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {slug === 'all'
              ? '浏览全部韩国漫画资源，发现更多好看的韩国漫画'
              : `精选${currentCategory.name}类韩国漫画，高质量在线漫画等你来读`}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="mt-6 flex items-center gap-2 flex-wrap">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                cat.slug === slug
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/70'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`${cat.icon} text-sm`}></i>
              </span>
              {cat.name}
            </Link>
          ))}
        </div>

        {/* Sort Controls */}
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm text-foreground-500">
            共找到 <strong className="text-foreground-900">{filtered.length}</strong> 部漫画
          </p>
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 border border-background-200/70">
            {([
              { value: 'latest' as const, label: '最新' },
              { value: 'rating' as const, label: '评分' },
              { value: 'views' as const, label: '人气' },
            ]).map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSortBy(opt.value)}
                className={`px-4 py-1.5 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === opt.value
                    ? 'bg-primary-500 text-white font-medium'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Comic Grid */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
          {paginatedComics.map((comic) => (
            <Link
              key={comic.id}
              to={`/article/${comic.slug}`}
              className="group cursor-pointer block"
            >
              <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  title={`${comic.title} - 韩国漫画网`}
                  className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute top-2 left-2">
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                    comic.status === '连载中' ? 'bg-primary-500 text-white' : 'bg-secondary-500 text-white'
                  }`}>
                    {comic.status}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
                  <div className="flex items-center gap-1 text-white text-xs">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-star-fill text-yellow-400 text-xs"></i>
                    </span>
                    <span>{comic.rating}</span>
                    <span className="mx-1 opacity-50">·</span>
                    <span>{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}次</span>
                  </div>
                </div>
              </div>
              <h3 className="text-sm font-medium text-foreground-900 leading-tight line-clamp-1 group-hover:text-primary-500 transition-colors">
                {comic.title}
              </h3>
              <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
            </Link>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200/70 text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-colors cursor-pointer ${
                  p === currentPage
                    ? 'bg-primary-500 text-white'
                    : 'border border-background-200/70 text-foreground-600 hover:bg-background-100'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200/70 text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>
    </>
  );
}